import { Suspense } from "react";

import { lazyWithPreload } from "react-lazy-with-preload";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { trackEvent } from "@needl-ai/common/analytics";
import { AuthProvider } from "@needl-ai/common/auth/auth";
import { AuthenticationStatusListener } from "@needl-ai/common/auth/components/AuthStatusListener";
import { SessionRefreshListener } from "@needl-ai/common/auth/components/SessionRefreshListener";
import { ConfigUIGate } from "@needl-ai/common/components/custom/ConfigUIGate";
import { ErrorBoundary } from "@needl-ai/common/components/custom/ErrorBoundary";
import { Toaster as Sonner } from "@needl-ai/common/components/ui/sonner";
import { Toaster } from "@needl-ai/common/components/ui/toaster";
import { TooltipProvider } from "@needl-ai/common/components/ui/tooltip";
import { AppDataProvider } from "@needl-ai/common/providers/AppDataProvider";
import { QueryProvider } from "@needl-ai/common/providers/QueryProvider";
import { ThemeProvider } from "@needl-ai/common/providers/ThemeContext";
import { UserProvider } from "@needl-ai/common/providers/UserProvider";
import { SettingsRoutes } from "@needl-ai/common/settings";
import { isMSquaredHub } from "@needl-ai/common/utils/isEnv";
import {
  useWorkspaceInit,
  WorkspaceSwitcher,
} from "@needl-ai/common/workspace";

import Copyright from "./components/Copyright";
import LoadingSkeleton from "./components/main/LoadingSkeleton";
import DisclaimerModal from "./components/modals/DisclaimerModal";
import { OfflineIndicator } from "./components/OfflineIndicator";
import WidgetLoadingSkeleton from "./components/widget/WidgetLoadingSkeleton";
import { ChatProvider } from "./contexts/ChatContext";
import { FileUploadProvider } from "./contexts/FileUploadContext";
import { UIProvider } from "./contexts/UIContext";
import { useAttachmentProtection } from "./hooks/useAttachmentProtection";
import Landing from "./pages/Landing";
import ProtectedRoute from "./pages/ProtectedRoute";

import { AppSidebarProvider } from "@/vendor/AppSidebar";

const onAnalyticsIdentified = () => trackEvent("landing");

// Lazy load route components for better performance with preloading
const Session = lazyWithPreload(() => import("./pages/Session"));
const Integrations = lazyWithPreload(() => import("./pages/Integrations"));
const NotFound = lazyWithPreload(() => import("./pages/NotFound"));
const Widget = lazyWithPreload(() => import("./pages/Widget"));
const Explore = lazyWithPreload(() => import("./pages/Explore"));
const FinancialMetrics = lazyWithPreload(
  () => import("./pages/FinancialMetrics")
);
const FinancialMetricsDashboard = lazyWithPreload(
  () => import("./pages/FinancialMetricsDashboard")
);
const FinancialMetricsHistoryPage = lazyWithPreload(
  () => import("./pages/FinancialMetricsHistoryPage")
);
const FinancialMetricsSession = lazyWithPreload(
  () => import("./pages/FinancialMetricsSession")
);
const FinancialMetricsResult = lazyWithPreload(
  () => import("./pages/FinancialMetricsResult")
);
const AssetRedirect = lazyWithPreload(() => import("./pages/AssetRedirect"));
const ArtifactReport = lazyWithPreload(() => import("./pages/ArtifactReport"));
const ArtifactReportSession = lazyWithPreload(
  () => import("./pages/ArtifactReportSession")
);
const ArtifactReportAssistantsPage = lazyWithPreload(
  () => import("./pages/ArtifactReportAssistantsPage")
);
const Settings = lazyWithPreload(() => import("./pages/Settings"));

// Preload all components for better performance
Session.preload();
Integrations.preload();
NotFound.preload();
Widget.preload();
Explore.preload();
FinancialMetrics.preload();
FinancialMetricsDashboard.preload();
FinancialMetricsSession.preload();
FinancialMetricsResult.preload();
AssetRedirect.preload();
ArtifactReport.preload();
ArtifactReportSession.preload();
ArtifactReportAssistantsPage.preload();
Settings.preload();

const BASE_URL = import.meta.env.VITE_BASE_PATH_RESEARCH_ASSIST || "";
const BASENAME = BASE_URL === "/" ? "" : BASE_URL.replace(/\/$/, "");

// Internal component to use the attachment protection hook
const AppInner = () => {
  useAttachmentProtection();

  return null;
};

// Component to handle workspace switcher under protected routes
const WorkspaceSwitcherWrapper = () => {
  const { showWorkspaceSwitcher } = useWorkspaceInit();

  return (
    <WorkspaceSwitcher
      isOpen={showWorkspaceSwitcher}
      disableClose={showWorkspaceSwitcher}
    />
  );
};

const App = () => {
  return (
    <AuthProvider>
      <QueryProvider>
        <ThemeProvider>
          <ErrorBoundary>
            <TooltipProvider>
              <OfflineIndicator />
              <Toaster />
              <Sonner />
              <BrowserRouter basename={BASENAME}>
                <UIProvider>
                  <FileUploadProvider>
                    <AppSidebarProvider>
                      <AppInner />
                      <AuthenticationStatusListener />
                      <SessionRefreshListener />
                      <Routes>
                        <Route
                          path="/widget/*"
                          element={
                            <Suspense fallback={<WidgetLoadingSkeleton />}>
                              <Widget />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/explore/*"
                          element={
                            <Suspense fallback={<WidgetLoadingSkeleton />}>
                              <Explore />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/financial-metrics"
                          element={
                            <Suspense fallback={<LoadingSkeleton />}>
                              <ProtectedRoute>
                                <UserProvider
                                  onAnalyticsEnabled={onAnalyticsIdentified}
                                >
                                  <FinancialMetrics />
                                </UserProvider>
                              </ProtectedRoute>
                            </Suspense>
                          }
                        >
                          <Route
                            index
                            element={<FinancialMetricsDashboard />}
                          />
                          <Route
                            path="history"
                            element={<FinancialMetricsHistoryPage />}
                          />
                          <Route
                            path="result"
                            element={<FinancialMetricsResult />}
                          />
                          <Route
                            path=":session_id"
                            element={<FinancialMetricsSession />}
                          />
                        </Route>
                        <Route
                          path={
                            isMSquaredHub() ? "/w/:workspace_subdomain/*" : "*"
                          }
                          element={
                            <Suspense fallback={<LoadingSkeleton />}>
                              <ProtectedRoute>
                                <UserProvider
                                  onAnalyticsEnabled={onAnalyticsIdentified}
                                >
                                  <ConfigUIGate
                                    feature="knowledge_management"
                                    description="Knowledge Management is not enabled for your organization. Please contact your administrator."
                                  >
                                    <AppDataProvider>
                                      <DisclaimerModal />
                                      <ChatProvider>
                                        <WorkspaceSwitcherWrapper />
                                        <Routes>
                                          <Route
                                            path="/"
                                            element={<Landing />}
                                          />
                                          <Route
                                            path="/integrations"
                                            element={<Integrations />}
                                          />
                                          <Route
                                            path="/integrations/app/:appName"
                                            element={<Integrations />}
                                          />
                                          <Route
                                            path="/c/:session_id"
                                            element={<Session />}
                                          />
                                          <Route
                                            path="/asset/redirect"
                                            element={<AssetRedirect />}
                                          />
                                          <Route
                                            path="/report-automation"
                                            element={<ArtifactReport />}
                                          />
                                          <Route
                                            path="/report-automation/scheduled-reports"
                                            element={
                                              <ConfigUIGate
                                                feature="artifact_report_scheduled_reports"
                                                description="Scheduled Reports is not enabled for your organization. Please contact your administrator."
                                              >
                                                <ArtifactReportAssistantsPage />
                                              </ConfigUIGate>
                                            }
                                          />
                                          <Route
                                            path="/report-automation/:session_id"
                                            element={<ArtifactReportSession />}
                                          />
                                          <Route
                                            path="/settings/*"
                                            element={
                                              <SettingsRoutes
                                                layout={<Settings />}
                                              />
                                            }
                                          />
                                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                                          <Route
                                            path="*"
                                            element={<NotFound />}
                                          />
                                        </Routes>
                                        <Copyright />
                                      </ChatProvider>
                                    </AppDataProvider>
                                  </ConfigUIGate>
                                </UserProvider>
                              </ProtectedRoute>
                            </Suspense>
                          }
                        />
                      </Routes>
                    </AppSidebarProvider>
                  </FileUploadProvider>
                </UIProvider>
              </BrowserRouter>
            </TooltipProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </QueryProvider>
    </AuthProvider>
  );
};

export default App;
